import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  siteConfig as defaultSiteConfig,
  navigationItems as defaultNavigationItems,
  languageButtons as defaultLanguageButtons,
  adminCards as defaultAdminCards,
  schoolsData as defaultSchoolsData,
  examResultsData as defaultExamResultsData,
  statisticsData as defaultStatisticsData,
  footerContent as defaultFooterContent
} from '../data/mock';

const EditableContext = createContext(null);

export const useEditable = () => {
  const context = useContext(EditableContext);
  if (!context) {
    throw new Error('useEditable must be used within an EditableProvider');
  }
  return context;
};

export const EditableProvider = ({ children }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [siteConfig, setSiteConfig] = useState(defaultSiteConfig);
  const [navigationItems, setNavigationItems] = useState(defaultNavigationItems);
  const [languageButtons, setLanguageButtons] = useState(defaultLanguageButtons);
  const [adminCards, setAdminCards] = useState(defaultAdminCards);
  const [schoolsData, setSchoolsData] = useState(defaultSchoolsData);
  const [examResultsData, setExamResultsData] = useState(defaultExamResultsData);
  const [statisticsData, setStatisticsData] = useState(defaultStatisticsData);
  const [footerContent, setFooterContent] = useState(defaultFooterContent);

  // Load saved data from localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem('laikosLaySchoolsData');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        if (parsed.siteConfig) setSiteConfig(parsed.siteConfig);
        if (parsed.navigationItems) setNavigationItems(parsed.navigationItems);
        if (parsed.languageButtons) setLanguageButtons(parsed.languageButtons);
        if (parsed.adminCards) setAdminCards(parsed.adminCards);
        if (parsed.schoolsData) setSchoolsData(parsed.schoolsData);
        if (parsed.examResultsData) setExamResultsData(parsed.examResultsData);
        if (parsed.statisticsData) setStatisticsData(parsed.statisticsData);
        if (parsed.footerContent) setFooterContent(parsed.footerContent);
      } catch (e) {
        console.error('Error loading saved data:', e);
      }
    }
  }, []);

  // Save data to localStorage whenever it changes
  const saveAllData = () => {
    const dataToSave = {
      siteConfig,
      navigationItems,
      languageButtons,
      adminCards,
      schoolsData,
      examResultsData,
      statisticsData,
      footerContent
    };
    localStorage.setItem('laikosLaySchoolsData', JSON.stringify(dataToSave));
  };

  // Reset to defaults
  const resetToDefaults = () => {
    setSiteConfig(defaultSiteConfig);
    setNavigationItems(defaultNavigationItems);
    setLanguageButtons(defaultLanguageButtons);
    setAdminCards(defaultAdminCards);
    setSchoolsData(defaultSchoolsData);
    setExamResultsData(defaultExamResultsData);
    setStatisticsData(defaultStatisticsData);
    setFooterContent(defaultFooterContent);
    localStorage.removeItem('laikosLaySchoolsData');
  };

  const value = {
    isEditMode,
    setIsEditMode,
    siteConfig,
    setSiteConfig,
    navigationItems,
    setNavigationItems,
    languageButtons,
    setLanguageButtons,
    adminCards,
    setAdminCards,
    schoolsData,
    setSchoolsData,
    examResultsData,
    setExamResultsData,
    statisticsData,
    setStatisticsData,
    footerContent,
    setFooterContent,
    saveAllData,
    resetToDefaults
  };

  return (
    <EditableContext.Provider value={value}>
      {children}
    </EditableContext.Provider>
  );
};

export default EditableContext;
